import React, { useContext, useState } from 'react';
import {
	Typography,
	TableContainer,
	Paper,
	Table,
	TableHead,
	Box,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Collapse,
	Tabs,
	Tab,
	Modal,
	Backdrop,
	Button,
	TableSortLabel,
	useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Pagination from '@mui/material/Pagination';
import SessionContext from '../../../../contexts/SessionContext';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import Loading from '../../../../components/common/Loading';
import CheckIcon from '@mui/icons-material/Check';

const formatAuditDate = (value) => {
	if (!value) {
		return 'Não disponível';
	}

	/*
	 * O backend retorna timestamps sem timezone explícito.
	 * Esses timestamps são gerados pelo banco em UTC.
	 *
	 * Caso já venha timezone/Z, preservamos.
	 * Caso contrário, adicionamos Z para interpretar como UTC
	 * antes de converter para o horário de Brasília.
	 */
	const normalizedValue = /[zZ]|[+-]\d\d:\d\d$/.test(value)
		? value
		: `${value}Z`;

	const date = new Date(normalizedValue);

	if (Number.isNaN(date.getTime())) {
		return 'Não disponível';
	}

	return date.toLocaleString('pt-BR', {
		timeZone: 'America/Sao_Paulo',
	});
};

/** Table with all the tracks */
export default function TracksTable(props) {
	// props:
	// headers_table, split_headers, tracks, translateCategory,
	// pages, currentPage, onPageChange, onRowClick,
	// deleteTrack, deletingTrack

	const theme = useTheme();
	const order = props.order || 'desc';
	const orderBy = props.orderBy || 'id';

	let { hasRole } = useContext(SessionContext);

	// Mapeamento entre headers e campos dos dados (suporta PT e EN)
	// IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
	// e não aos campos do JSON retornado.
	const getFieldName = (headerTitle) => {
		const headerMap = {
			// Português
			'Selo': 'label',
			'Faixa': 'title',
			'Artista': 'artist',
			'ISRC': 'isrc',

			// Inglês
			'Label': 'label',
			'Track': 'title',
			'Artist': 'artist',
		};

		return headerMap[headerTitle] || null;
	};

	// Handler para mudança de ordenação - comunica ao componente pai
	const handleRequestSort = (property) => {
		if (!props.onSortChange) return;

		const isAsc = orderBy === property && order === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		const newOrderBy = property;

		props.onSortChange(newOrderBy, newOrder);
	};

	return (
		<>
			<TableContainer
				className='table-container'
				component={Paper}
			>
				<Table
					size='small'
					aria-label='collapsible table'
					className='basic-table'
				>
					<TableHead className='basic-table-head'>
						<TableRow>
							<TableCell></TableCell>

							{props.headers_table.map(
								(title, index) => {
									const fieldName =
										getFieldName(title);

									const isSortable =
										fieldName !== null;

									return (
										<TableCell
											key={index}
											align='center'
											sortDirection={
												orderBy ===
												fieldName
													? order
													: false
											}
										>
											{isSortable ? (
												<TableSortLabel
													active={
														orderBy ===
														fieldName
													}
													direction={
														orderBy ===
														fieldName
															? order
															: 'asc'
													}
													onClick={() =>
														handleRequestSort(
															fieldName
														)
													}
													sx={{
														'&.Mui-active':
															{
																color:
																	theme
																		.palette
																		.secondary
																		.main,
															},
														'& .MuiTableSortLabel-icon':
															{
																color:
																	orderBy ===
																	fieldName
																		? `${theme.palette.secondary.main} !important`
																		: 'rgba(255, 255, 255, 0.54)',
															},
														'&:hover':
															{
																color:
																	theme
																		.palette
																		.secondary
																		.light,
															},
														'&:hover .MuiTableSortLabel-icon':
															{
																color: `${theme.palette.secondary.light} !important`,
															},
														color:
															orderBy ===
															fieldName
																? theme
																		.palette
																		.secondary
																		.main
																: 'inherit',
													}}
												>
													{title}
												</TableSortLabel>
											) : (
												title
											)}
										</TableCell>
									);
								}
							)}

							<TableCell align='center'>
								Data de cadastro
							</TableCell>

							<TableCell align='center'>
								Última alteração
							</TableCell>

							{hasRole([
								'TRACKS_FULL_VIEW',
								'TRACKS_WRITE',
							]) && (
								<TableCell align='center'></TableCell>
							)}
						</TableRow>
					</TableHead>

					<TableBody>
						{props.tracks?.length > 0
							? props.tracks.map((track) => {
									return (
										<TrackRow
											split_headers={
												props.split_headers
											}
											key={track.id}
											track={track}
											onClick={
												props.onRowClick
											}
											translateCategory={
												props.translateCategory
											}
											deleteTrack={
												props.deleteTrack
											}
											hasRole={hasRole}
											loading={
												props.deletingTrack
											}
										/>
									);
							  })
							: null}
					</TableBody>
				</Table>
			</TableContainer>

			<Pagination
				shape='rounded'
				className='table-pagination'
				count={props.pages}
				page={props.currentPage}
				onChange={(e, value) => {
					props.onPageChange(value);
				}}
			/>
		</>
	);
}

/** Tracks table row */
function TrackRow(props) {
	const [collapsed, setCollapsed] = useState(true);
	const [value, setValue] = useState(0);

	const [
		showConfirmationPanel,
		setShowConfirmationPanel,
	] = useState(false);

	const hasWriteAccess = props.hasRole([
		'TRACKS_FULL_VIEW',
		'TRACKS_WRITE',
	]);

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	return (
		<>
			<TableRow className='row'>
				<TableCell className='cell'>
					<IconButton
						aria-label='expand row'
						size='small'
						onClick={() => {
							setCollapsed(!collapsed);
						}}
					>
						{collapsed ? (
							<KeyboardArrowDownIcon />
						) : (
							<KeyboardArrowUpIcon />
						)}
					</IconButton>
				</TableCell>

				{hasWriteAccess ? (
					<>
						<TableCell
							className='cell clickable'
							align='center'
							onClick={() =>
								props.onClick(
									props.track
								)
							}
						>
							{props.track.label}{' '}
						</TableCell>

						<TableCell
							className='cell clickable'
							align='center'
							onClick={() =>
								props.onClick(
									props.track
								)
							}
						>
							{props.track.title}{' '}
						</TableCell>

						<TableCell
							className='cell clickable'
							align='center'
							onClick={() =>
								props.onClick(
									props.track
								)
							}
						>
							{props.track.artist}{' '}
						</TableCell>

						<TableCell
							className='cell clickable'
							align='center'
							onClick={() =>
								props.onClick(
									props.track
								)
							}
						>
							{props.track.isrc}{' '}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{formatAuditDate(
								props.track.created_at
							)}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{formatAuditDate(
								props.track.updated_at
							)}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							<DeleteIcon
								className='clickable deleteIcon'
								fontSize='small'
								onClick={() => {
									setShowConfirmationPanel(
										true
									);
								}}
							/>
						</TableCell>
					</>
				) : props.hasRole([
						'TRACKS_CLIENT_VIEW',
						'TRACKS_READ',
				  ]) ? (
					<>
						<TableCell
							className='cell'
							align='center'
						>
							{props.track.label}{' '}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{props.track.title}{' '}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{props.track.artist}{' '}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{props.track.isrc}{' '}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{formatAuditDate(
								props.track.created_at
							)}
						</TableCell>

						<TableCell
							className='cell'
							align='center'
						>
							{formatAuditDate(
								props.track.updated_at
							)}
						</TableCell>
					</>
				) : null}
			</TableRow>

			<TableRow>
				<TableCell
					style={{
						paddingBottom: 0,
						paddingTop: 0,
					}}
					colSpan={hasWriteAccess ? 8 : 7}
				>
					<Collapse
						timeout='auto'
						in={!collapsed}
					>
						<Typography
							variant='h6'
							gutterBottom
							component='div'
						>
							{' '}
							Split{' '}
						</Typography>

						<Box margin={1}>
							<Tabs
								value={value}
								onChange={handleChange}
							>
								{props.track.splits.length >
								0
									? props.track.splits.map(
											(
												split,
												index
											) => (
												<Tab
													key={
														props
															.track
															.id ??
														index
													}
													label={
														split.upc
															? split.upc
															: 'Padrão'
													}
												/>
											)
									  )
									: null}
							</Tabs>

							<Table
								className='split-table'
								aria-label='purchases'
							>
								<TableHead>
									<TableRow>
										{props.split_headers.map(
											(
												title,
												key
											) => (
												<TableCell
													key={
														key
													}
													align='center'
												>
													{
														title
													}
												</TableCell>
											)
										)}
									</TableRow>
								</TableHead>

								<TableBody>
									{props.track.splits
										.length > 0 &&
										props.track.splits[
											value
										].contributions.map(
											(
												contrib,
												idx
											) => (
												<TableRow
													key={
														contrib.id ??
														idx
													}
												>
													<TableCell className='cell'>
														{props.translateCategory(
															contrib.category
														)}
													</TableCell>

													<TableCell className='cell'>
														{
															contrib.name
														}
													</TableCell>

													<TableCell className='cell'>
														{
															contrib.artist_name
														}
													</TableCell>

													<TableCell className='cell'>
														{
															contrib.share
														}
													</TableCell>

													<TableCell className='cell'>
														{contrib.is_orb_managed ? (
															<CheckIcon color='success' />
														) : null}
													</TableCell>
												</TableRow>
											)
										)}
								</TableBody>
							</Table>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>

			<Modal
				open={showConfirmationPanel}
				onClose={() =>
					setShowConfirmationPanel(false)
				}
				slots={{
					backdrop: Backdrop,
				}}
				slotProps={{
					backdrop: {
						timeout: 500,
					},
				}}
			>
				<div className='modal-container confirmation-panel'>
					<Typography>
						Tem certeza que deseja deletar a
						faixa {props.track.title}
					</Typography>

					{props.loading ? (
						<Loading text='Deletando faixa...' />
					) : (
						<div className='buttons-container'>
							<Button
								variant='contained'
								color='secondary'
								onClick={() => {
									props.deleteTrack(
										props.track.id
									);
								}}
							>
								Deletar
							</Button>

							<Button
								variant='contained'
								color='default'
								onClick={() => {
									setShowConfirmationPanel(
										false
									);
								}}
							>
								Cancelar
							</Button>
						</div>
					)}
				</div>
			</Modal>
		</>
	);
}
